import { BlankContent, Spinner } from '@cint/wonderment';
import { useMemo } from 'react';

import {
  BarChart,
  ColumnChart,
  LineChart,
  StackedColumnChart,
} from '@/components';
import { formatCurrencyValue } from '@/utils/formatCurrencyValue';
import {
  getCategories,
  groupMetricsByKey,
  prepareSeries,
} from '@/utils/reports/chartDataUtils';
import { useGroupedMetricByDateRange } from '@/utils/reports/useGroupedMetricByDateRange';

interface ChartsProps {
  isDataLoading: boolean;
  marketingCampaignData: any; // todo: update types when available
  marketingCampaignDataError?: boolean;
}

type Amount = {
  value: number;
  currency_code: string;
  currency_scale?: number | undefined;
};

type ProfitabilityData = {
  recruitment_source: string;
  total_revenue: Amount;
  current_profitability: Amount;
  total_costs: Amount;
};

type PanelistActivityData = {
  recruitment_source: string;
  active_users: number;
  sign_ups: number;
  churned_users: number;
};

type RecruitmentConversionData = {
  recruitment_source: string;
  sign_ups: number;
  confirmed_emails: number;
  panelist_with_participation: number;
  panelist_with_completes: number;
  total_participations_completed: number;
};

export const Charts = ({
  marketingCampaignData,
  isDataLoading,
  marketingCampaignDataError,
}: ChartsProps) => {
  const engagement = marketingCampaignData?.engagement;
  const panelistTotals = marketingCampaignData?.panelist_activities_by_sources;
  const profitability = marketingCampaignData?.profitability_by_sources;
  const recruitmentConversion = marketingCampaignData?.engagement_by_sources;

  const currency = profitability?.[0]?.current_profitability?.currency_code;
  const currencyScale =
    profitability?.[0]?.current_profitability?.currency_scale;

  const signUpsData = useGroupedMetricByDateRange({
    data: engagement,
    valueKey: 'sign_ups',
    groupKey: 'recruitment_source',
  });
  const confirmedEmailsData = useGroupedMetricByDateRange({
    data: engagement,
    valueKey: 'confirmed_emails',
    groupKey: 'recruitment_source',
  });
  const surveyAttemptersData = useGroupedMetricByDateRange({
    data: engagement,
    valueKey: 'panelist_with_participation',
    groupKey: 'recruitment_source',
  });
  const surveyCompletesData = useGroupedMetricByDateRange({
    data: engagement,
    valueKey: 'total_participations_completed',
    groupKey: 'recruitment_source',
  });

  const panelistsActivityData = useMemo(() => {
    return panelistTotals
      ? groupMetricsByKey<
          PanelistActivityData,
          'active_users' | 'sign_ups' | 'churned_users',
          'recruitment_source'
        >(
          panelistTotals,
          ['sign_ups', 'active_users', 'churned_users'],
          'recruitment_source',
          {
            active_users: 'Active',
            sign_ups: 'Registered',
            churned_users: 'Churned',
          }
        )
      : [];
  }, [panelistTotals]);

  const financialActivityData = useMemo(() => {
    if (!profitability) return [];

    const grouped = groupMetricsByKey<
      ProfitabilityData,
      'total_revenue' | 'current_profitability' | 'total_costs',
      'recruitment_source'
    >(
      profitability,
      ['total_revenue', 'current_profitability', 'total_costs'],
      'recruitment_source',
      {
        total_revenue: 'Revenue',
        current_profitability: 'Profitability',
        total_costs: 'Cost',
      }
    );

    return grouped.map((group) => ({
      ...group,
      data: group.data.map((d) => ({
        y: d.y?.value,
      })),
    }));
  }, [profitability]);

  const recruitmentConversionData = useMemo(() => {
    return recruitmentConversion
      ? groupMetricsByKey<
          RecruitmentConversionData,
          | 'sign_ups'
          | 'confirmed_emails'
          | 'panelist_with_participation'
          | 'panelist_with_completes',
          'recruitment_source'
        >(
          recruitmentConversion,
          [
            'sign_ups',
            'confirmed_emails',
            'panelist_with_participation',
            'panelist_with_completes',
          ],
          'recruitment_source',
          {
            sign_ups: 'Registrations',
            confirmed_emails: 'Confirmed',
            panelist_with_participation: 'Panelists with Participations',
            panelist_with_completes: 'Panelists with Completes',
          }
        )
      : [];
  }, [recruitmentConversion]);

  if (marketingCampaignDataError) {
    return (
      <BlankContent
        description="Please try again later"
        size="lg"
        title="Something went wrong"
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-16 px-3 md:px-6 lg:px-8 mt-15">
      {isDataLoading && <Spinner size="xl" />}
      {!isDataLoading && marketingCampaignData && (
        <>
          <LineChart
            title="Sign-ups"
            subtitle="Over time"
            series={prepareSeries(signUpsData)}
            categories={getCategories(signUpsData)}
          />
          <LineChart
            title="Confirmed registration emails"
            subtitle="Over time"
            series={prepareSeries(confirmedEmailsData)}
            categories={getCategories(confirmedEmailsData)}
          />
          <ColumnChart
            title="Active Users (Survey attempters)"
            subtitle="Over time"
            series={prepareSeries(surveyAttemptersData)}
            categories={getCategories(surveyAttemptersData)}
          />
          <ColumnChart
            title="Survey completes"
            subtitle="Over time"
            series={prepareSeries(surveyCompletesData)}
            categories={getCategories(surveyCompletesData)}
          />
          <BarChart
            hasTotal={false}
            title="Panelist activity"
            customColors={['#3D88C8', '#489295', '#EAB331']}
            series={prepareSeries(panelistsActivityData)}
            categories={getCategories(panelistsActivityData)}
          />
          <StackedColumnChart
            hasTotal={false}
            title="Financial activity"
            customColors={['#7599B6', '#59AC77', '#989190']}
            headerTooltip="Total amount of money that has been paid out to panelists. The incentives for the current 30 days period will be displayed on the 26th day of the following month, after the quality checks are finished."
            formatValue={(val) =>
              formatCurrencyValue(val, currencyScale, currency)
            }
            labelFormatter={function () {
              const series = this as Highcharts.Series & {
                options: { custom?: { total: number } };
              };
              const total =
                this.name === 'Cost'
                  ? Math.abs(series.options.custom?.total ?? 0)
                  : series.options.custom?.total;

              return `
                <span style="font-size: 14px; font-weight: 600;">${
                  this.name
                }</span><br>
                <strong style="font-size: 16px; font-weight: 800;">${formatCurrencyValue(
                  total,
                  currencyScale,
                  currency
                )}</strong>
              `;
            }}
            series={prepareSeries(financialActivityData)}
            categories={getCategories(financialActivityData)}
          />
          <BarChart
            hasTotal={false}
            headerTooltip="This chart visualizes the recruitment process, showing the number of users at each stage. The funnel narrows as users progress, helping identify drop-off points and optimize conversions. Hover over each stage for specific counts and percentages."
            title="Recruitment conversion path"
            series={prepareSeries(recruitmentConversionData)}
            categories={getCategories(recruitmentConversionData)}
          />
        </>
      )}
    </div>
  );
};
